import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Tabs } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type CardTabs from 'modules/core/components/Card/CardTabs'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { isManager } from 'modules/employees/lib/isManager'
import questionGroups from 'modules/surveys/store/collections/questionGroups'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

type CardTabsProps = React.ComponentProps<typeof CardTabs>

interface Props {
  form: SurveyForm | Survey
  canEditSurvey: boolean
}

function SurveyShowPageTabs({ form, canEditSurvey }: Props) {
  const formId = form.id
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const {
    employee,
    access: { id: userId },
  } = useCurrentSession()

  const questionGroup = questionGroups.find({
    surveys_form_id: formId,
    respondent_id: userId,
  })

  const canSeeInfo =
    form?.attributes.visible_to_managers &&
    (isManager(employee) || userIsTeamLead(teams, memberships, employee.id))

  if (!canEditSurvey && !canSeeInfo) return null

  let tabs: CardTabsProps['tabs'] = []

  if (canEditSurvey) {
    tabs = tabs.concat([
      {
        href: navigation.engagement.surveys.id.configuration.fullPath({ id: formId }),
        name: i18n.t('surveys.form.tabs.index'),
      },
      {
        href: navigation.engagement.surveys.id.questions.fullPath({ id: formId }),
        name: i18n.t('surveys.form.tabs.questions'),
      },
    ])
  }

  tabs.push({
    href: navigation.engagement.surveys.id.participants.fullPath({ id: formId }),
    name: i18n.t('surveys.form.tabs.participants'),
  })

  if (form && !form.isDraft) {
    tabs.push({
      href: navigation.engagement.surveys.id.responses.fullPath({ id: formId }),
      name: i18n.t('surveys.form.tabs.responses'),
    })
  }

  if (questionGroup && !questionGroup.isAnswered) {
    tabs.push({
      href: navigation.engagement.surveys.id.answer.fullPath({ id: formId }),
      name: i18n.t('surveys.form.tabs.answer'),
    })
  }

  tabs.push({
    href: navigation.engagement.surveys.id.analytics.fullPath({ id: formId }),
    name: i18n.t('surveys.form.tabs.analytics'),
  })

  // return <CardTabs tabs={tabs} />
  const currentId = tabs.find((tab) => pathname.includes(tab.href))?.href || pathname
  return (
    <Tabs currentId={currentId} onTabClicked={navigate}>
      {tabs.map((tab) => (
        <Tabs.Tab key={tab.href} id={tab.href} label={tab.name} />
      ))}
    </Tabs>
  )
}

export default observer(SurveyShowPageTabs)
